import { get as apiGet, post } from './api'
import type { Song, Collection, Playlist, SongElement } from '../types/songbook'

export interface SongbookBackup {
  version: 1
  exportedAt: string
  collections: Collection[]
  songs: Song[]
  playlists: Playlist[]
}

function mapCollection(r: Record<string, unknown>): Collection {
  return {
    id:         r.id as string,
    userId:     r.user_id as string,
    name:       r.name as string,
    artist:     (r.artist as string)       ?? '',
    shareToken: (r.share_token as string)  ?? null,
    createdAt:  r.created_at as string,
    updatedAt:  r.updated_at as string,
  }
}

function mapSong(r: Record<string, unknown>): Song {
  return {
    id:           r.id as string,
    userId:       r.user_id as string,
    collectionId: (r.collection_id as string) ?? null,
    title:        r.title as string,
    notes:        (r.notes as string)       ?? '',
    info:         (r.info as string)        ?? '',
    listenUrl:    (r.listen_url as string)  ?? '',
    duration:     (r.duration as number)    ?? null,
    shareToken:   (r.share_token as string) ?? null,
    attachments:  (r.attachments ?? [])     as Song['attachments'],
    elements:     (r.elements ?? [])        as SongElement[],
    createdAt:    r.created_at as string,
    updatedAt:    r.updated_at as string,
  }
}

function mapPlaylist(r: Record<string, unknown>): Playlist {
  return {
    id:        r.id as string,
    userId:    r.user_id as string,
    name:      r.name as string,
    songIds:   (r.song_ids ?? []) as string[],
    createdAt: r.created_at as string,
    updatedAt: r.updated_at as string,
  }
}

export async function exportData(): Promise<void> {
  const [collectionsData, songsData, playlistsData] = await Promise.all([
    apiGet<Record<string, unknown>[]>('/collections'),
    apiGet<Record<string, unknown>[]>('/songs'),
    apiGet<Record<string, unknown>[]>('/playlists'),
  ])

  const backup: SongbookBackup = {
    version:     1,
    exportedAt:  new Date().toISOString(),
    collections: collectionsData.map(mapCollection),
    songs:       songsData.map(mapSong),
    playlists:   playlistsData.map(mapPlaylist),
  }

  const json = JSON.stringify(backup, null, 2)
  const blob = new Blob([json], { type: 'application/json' })
  const url  = URL.createObjectURL(blob)
  const date = new Date().toISOString().slice(0, 10)

  const a    = document.createElement('a')
  a.href     = url
  a.download = `songbook-backup-${date}.json`
  a.click()
  URL.revokeObjectURL(url)
}

export async function importData(file: File, userId: string): Promise<{ collections: number; songs: number; playlists: number }> {
  const text   = await file.text()
  const backup: SongbookBackup = JSON.parse(text)

  if (!backup.version || !backup.collections || !backup.songs) {
    throw new Error('Invalid backup file')
  }

  let collectionsImported = 0
  let songsImported       = 0
  let playlistsImported   = 0

  const collectionIdMap = new Map<string, string>()
  const songIdMap       = new Map<string, string>()

  const existingCols = await apiGet<Record<string, unknown>[]>('/collections')
  const existingColKeys = new Set(
    existingCols.map((c) => `${c.name as string}|||${(c.artist as string) ?? ''}`)
  )

  for (const col of backup.collections) {
    const key = `${col.name}|||${col.artist ?? ''}`
    if (existingColKeys.has(key)) {
      const existing = existingCols.find(
        (c) => `${c.name as string}|||${(c.artist as string) ?? ''}` === key
      )
      if (existing) collectionIdMap.set(col.id, existing.id as string)
      continue
    }

    try {
      const created = await post<Record<string, unknown>>('/collections', {
        user_id: userId,
        name:    col.name,
        artist:  col.artist || '',
      })
      collectionIdMap.set(col.id, created.id as string)
      collectionsImported++
    } catch (e) {
      console.warn('importData: collection insert failed', e)
    }
  }

  const existingSongs    = await apiGet<Record<string, unknown>[]>('/songs')
  const existingSongKeys = new Set(
    existingSongs.map((s) => `${s.title as string}|||${(s.collection_id as string) ?? ''}`)
  )

  // Build map of old song ID → existing song ID for playlist remapping
  for (const existingSong of existingSongs) {
    songIdMap.set(existingSong.id as string, existingSong.id as string)
  }

  for (const song of backup.songs) {
    const newCollectionId = song.collectionId
      ? (collectionIdMap.get(song.collectionId) ?? null)
      : null

    const key = `${song.title}|||${newCollectionId ?? ''}`
    if (existingSongKeys.has(key)) {
      // Map old ID to existing song ID
      const existing = existingSongs.find(
        (s) => `${s.title as string}|||${(s.collection_id as string) ?? ''}` === key
      )
      if (existing) songIdMap.set(song.id, existing.id as string)
      continue
    }

    try {
      const created = await post<Record<string, unknown>>('/songs', {
        user_id:       userId,
        collection_id: newCollectionId,
        title:         song.title,
        notes:         song.notes || '',
        info:          song.info  || '',
        listen_url:    song.listenUrl || '',
        duration:      song.duration ?? null,
        elements:      song.elements,
        attachments:   song.attachments ?? [],
      })
      songIdMap.set(song.id, created.id as string)
      songsImported++
    } catch (e) {
      console.warn('importData: song insert failed', e)
    }
  }

  // Import playlists — remap song IDs to new IDs
  if (backup.playlists?.length) {
    const existingPlaylists    = await apiGet<Record<string, unknown>[]>('/playlists')
    const existingPlaylistKeys = new Set(existingPlaylists.map((p) => p.name as string))

    for (const playlist of backup.playlists) {
      if (existingPlaylistKeys.has(playlist.name)) continue

      const remappedIds = playlist.songIds
        .map((id) => songIdMap.get(id))
        .filter((id): id is string => !!id)

      try {
        await post('/playlists', {
          user_id:  userId,
          name:     playlist.name,
          song_ids: remappedIds,
        })
        playlistsImported++
      } catch (e) {
        console.warn('importData: playlist insert failed', e)
      }
    }
  }

  return { collections: collectionsImported, songs: songsImported, playlists: playlistsImported }
}
